import { ServerMessagePayload } from '@common/api';

import { ClientWorld } from '../ClientWorld';
import { ClientRegistry } from '../components';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

/**
 * Smoothly interpolates VisualPosition toward GlobalTransform.
 *
 * GlobalTransform is computed by TransformPropagationSystem from the hierarchy.
 * VisualPosition is then used by RenderSystem for actual sprite positioning.
 *
 * Child entities of the local player use the faster LOCAL smoothing speed to
 * ensure they track precisely with the player (e.g., held items, equipment).
 */
export class SmoothingSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = ['GlobalTransform'];
  private readonly SMOOTHING_SPEED_REMOTE = 16;
  private readonly SMOOTHING_SPEED_LOCAL = 32;
  private readonly TELEPORT_THRESHOLD = 100.0;

  updateEntity({
    world,
    entityId,
    deltaTime,
  }: FilteredClientComponentSystemUpdateEntityArgs): ServerMessagePayload[] {
    const globalTransform = world.getComponent(entityId, 'GlobalTransform');
    if (!globalTransform) return [];

    let visualPos = world.getComponent(entityId, 'VisualPosition');
    const localEntityId = this.clientServices.localPlayer.entityId;

    if (!visualPos) {
      visualPos = world.createComponent('VisualPosition', {
        x: globalTransform.x,
        y: globalTransform.y,
      });
      this.addComponent(entityId, visualPos);
    }

    // Use fast smoothing for local player and any descendants (e.g., held items)
    const isLocalEntity = this.isDescendantOfLocalPlayer(world, entityId, localEntityId);

    const dx = globalTransform.x - visualPos.x;
    const dy = globalTransform.y - visualPos.y;
    const distSq = dx * dx + dy * dy;

    if (distSq > this.TELEPORT_THRESHOLD * this.TELEPORT_THRESHOLD) {
      visualPos.x = globalTransform.x;
      visualPos.y = globalTransform.y;
    } else {
      const speed = isLocalEntity ? this.SMOOTHING_SPEED_LOCAL : this.SMOOTHING_SPEED_REMOTE;
      const t = 1.0 - Math.exp(-speed * deltaTime);
      visualPos.x += dx * t;
      visualPos.y += dy * t;
    }

    return [];
  }

  /**
   * Check if an entity is the local player or a descendant of the local player.
   * Descendants should use local smoothing to track precisely with the player.
   */
  private isDescendantOfLocalPlayer(
    world: ClientWorld,
    entityId: number,
    localEntityId: number | undefined,
  ): boolean {
    if (localEntityId === undefined) return false;
    if (entityId === localEntityId) return true;

    const parent = world.getParent(entityId);
    if (parent !== undefined) {
      return this.isDescendantOfLocalPlayer(world, parent, localEntityId);
    }
    return false;
  }
}
